window.U20CapsuleApp = function U20CapsuleApp(root, config) {
'use strict';

const lifecycleController = new AbortController();
const lifecycleSignal = lifecycleController.signal;
let destroyed = false;
const activeTimers = new Set();
const pendingImages = new Map();

function later(callback, delay) {
  if (destroyed) return 0;
  const timer = window.setTimeout(() => {
    activeTimers.delete(timer);
    if (!destroyed) callback();
  }, delay);
  activeTimers.add(timer);
  return timer;
}

function clearLater(timer) {
  if (!timer) return;
  window.clearTimeout(timer);
  activeTimers.delete(timer);
}

const realDocument = window.document;
const document = new Proxy(realDocument, {
  get(target, property) {
    if (property === 'querySelector') return root.querySelector.bind(root);
    if (property === 'querySelectorAll') return root.querySelectorAll.bind(root);
    const value = target[property];
    return typeof value === 'function' ? value.bind(target) : value;
  }
});

const stage = document.querySelector('.machine-stage');
const powerHotspot = document.querySelector('.power-hotspot');
const topLogoHotspot = document.querySelector('.top-logo-hotspot');
const leverHotspot = document.querySelector('.lever-hotspot');
const knobHotspot = document.querySelector('.knob-hotspot');
const domeHotspot = document.querySelector('.dome-hotspot');
const outletHotspot = document.querySelector('.outlet-hotspot');
const statusCopy = document.querySelector('.status-copy');
const productName = document.querySelector('.product-name');
const productPrice = document.querySelector('.product-price');
const productVisual = document.querySelector('.product-visual');
const productImages = [...document.querySelectorAll('.product-image')];
const outletCapsule = document.querySelector('.outlet-capsule');
const capsuleMixerField = document.querySelector('.capsule-mixer__field');
const capsuleMixerSelected = document.querySelector('.capsule-mixer__selected');

// One softened sprite is recolored in CSS. The twelve colors intentionally
// match the original supplied set without shipping twelve near-identical PNGs.
const capsuleSprite = config.assets.capsuleSprite;
const capsuleColors = [
  '#ff3fa4', // candy pink
  '#ff352f', // vivid red
  '#25f0c2', // bright mint
  '#f00082', // hot magenta
  '#fffdf2', // pearl white
  '#7df51f', // lime green
  '#d97722', // caramel brown
  '#ff8a0a', // tangerine
  '#24354f', // bright ink black
  '#058cff', // electric blue
  '#ffe11a', // lemon yellow
  '#8c2cff'  // vivid purple
];

const products = config.products;

const allProducts = [...products.accessories, ...products.toys];

let powered = false;
let starting = false;
let category = null;
let knobAngle = 0;
let busy = false;
let knobReleaseTimer = 0;
let categoryDrawTimers = [];
let pendingCategoryProduct = null;
let screenSwipe = null;
let lastScreenSwipeAt = -Infinity;
let productGlitchTimer = 0;
const preloadedProductUrls = new Set();
let currentProduct = null;
let selectedCapsuleIndex = 1;
let nextCapsuleIndex = 0;
let easterLeverSteps = [];
let easterSequenceStartedAt = 0;
let easterArmedUntil = 0;
let easterTimers = [];
let productLayoutToken = 0;
let productFrameObserver = null;
let onWindowResize = null;
const productBoundsCache = new Map();
const drawState = {
  all: { queue: [], last: null },
  accessories: { queue: [], last: null },
  toys: { queue: [], last: null }
};
const scrambleJobs = new WeakMap();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const RETURN_STATE_KEY = 'u20CapsuleReturnState';
let returnStateMemory = null;
const TITLE_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモラリルレロワンABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz01!<>-_\\/[]{}—=+*^?#';
const PRICE_CHARS = '0123456789$¥€£ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!<>-_+=?#';
const EASTER_SEQUENCE = ['accessories', 'toys', 'accessories', 'toys'];
const EASTER_SEQUENCE_WINDOW = 4000;

function clearEasterTimers() {
  easterTimers.forEach(clearLater);
  easterTimers = [];
}

function resetEasterSequence() {
  easterLeverSteps = [];
  easterSequenceStartedAt = 0;
  easterArmedUntil = 0;
}

function trackEasterLever(previousCategory, nextCategory) {
  const now = performance.now();

  if (easterSequenceStartedAt && now - easterSequenceStartedAt > EASTER_SEQUENCE_WINDOW) {
    resetEasterSequence();
  }

  /* If the lever already rests in the UP position, that visible position is
     allowed to be the first UP in UP-DOWN-UP-DOWN. */
  if (!easterLeverSteps.length && previousCategory === 'accessories' && nextCategory === 'toys') {
    easterSequenceStartedAt = now;
    easterLeverSteps = ['accessories'];
  }

  const expected = EASTER_SEQUENCE[easterLeverSteps.length];
  if (nextCategory === expected) {
    if (!easterSequenceStartedAt) easterSequenceStartedAt = now;
    easterLeverSteps.push(nextCategory);
  } else if (nextCategory === EASTER_SEQUENCE[0]) {
    easterSequenceStartedAt = now;
    easterLeverSteps = [nextCategory];
  } else {
    resetEasterSequence();
    return;
  }

  if (easterLeverSteps.length === EASTER_SEQUENCE.length) {
    easterArmedUntil = easterSequenceStartedAt + EASTER_SEQUENCE_WINDOW;
    easterLeverSteps = [];
    easterSequenceStartedAt = 0;
  }
}

function finishEasterEgg() {
  clearEasterTimers();
  stage.classList.remove(
    'is-easter-chaos',
    'is-easter-reveal',
    'has-easter-copy',
    'has-preview',
    'is-mixing'
  );
  busy = false;
  setStatus(category === 'accessories'
    ? 'ACCESSORIES · TURN KNOB TO BROWSE'
    : 'TOYS · TURN KNOB TO BROWSE');
}

function cancelEasterEgg() {
  const active = stage.classList.contains('is-easter-chaos')
    || stage.classList.contains('is-easter-reveal');
  clearEasterTimers();
  resetEasterSequence();
  if (!active) return;
  stage.classList.remove(
    'is-easter-chaos',
    'is-easter-reveal',
    'has-easter-copy',
    'has-preview',
    'is-mixing'
  );
  busy = false;
}

function playEasterEgg() {
  if (!powered || starting || busy || performance.now() > easterArmedUntil) {
    resetEasterSequence();
    return;
  }

  resetEasterSequence();
  clearCategoryDraw();
  clearEasterTimers();
  config.onEasterPrepare?.();
  busy = true;
  currentProduct = null;
  buildCapsuleMixer();
  stage.classList.remove('is-selecting-capsule', 'is-screen-dropping', 'is-dropping', 'is-open-it');
  stage.classList.add('has-preview', 'is-mixing', 'is-easter-chaos');
  setStatus('SECRET SIGNAL...');

  easterTimers.push(later(() => {
    stage.classList.remove('is-easter-chaos', 'is-mixing');
    stage.classList.add('is-easter-reveal');
  }, 2000));

  easterTimers.push(later(() => {
    stage.classList.add('has-easter-copy');
  }, 2700));

  /* Hold the complete two-image composition for 1.5 seconds. */
  easterTimers.push(later(() => { finishEasterEgg(); config.onEasterComplete?.(); }, 4200));
}

function setOutletCapsuleColor(index) {
  selectedCapsuleIndex = Math.max(0, Math.min(capsuleColors.length - 1, index));
  outletCapsule.style.setProperty('--outlet-capsule-color', capsuleColors[selectedCapsuleIndex]);
}

const stageObserver = new IntersectionObserver(entries => {
  if (destroyed) return;
  const visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0.12);
  stage.classList.toggle('is-visible', visible && !document.hidden);
}, { threshold: [0, 0.12, 0.5] });

stageObserver.observe(stage);

function setStatus(text) {
  scrambleTo(statusCopy, text, 'status');
}

function randomCharacter(chars) {
  return chars[Math.floor(Math.random() * chars.length)];
}

function productKey(item) {
  return `${item[0]}|${item[2]}`;
}

function drawNextProduct(scope) {
  const source = scope === 'all' ? allProducts : products[scope];
  const state = drawState[scope];

  if (!Array.isArray(source) || !source.length || !state) return null;

  if (!state.queue.length) {
    state.queue = [...source];
  }

  const item = state.queue.shift();
  state.last = productKey(item);
  preloadNextProductImages(scope, 2);
  return item;
}

function preloadProductItem(item) {
  const url = item?.[2];
  if (!url || preloadedProductUrls.has(url) || destroyed) return;
  preloadedProductUrls.add(url);
  const image = new Image();
  image.decoding = 'async';
  const release = () => {
    image.onload = null;
    image.onerror = null;
    pendingImages.delete(image);
  };
  image.onload = release;
  image.onerror = release;
  pendingImages.set(image, null);
  image.src = url;
}

function preloadNextProductImages(scope = 'all', limit = 2) {
  const source = scope === 'all' ? allProducts : products[scope];
  const state = drawState[scope];
  if (!Array.isArray(source) || !source.length || !state || destroyed) return;
  if (!state.queue.length) state.queue = [...source];
  state.queue.slice(0, limit).forEach(item => {
    preloadProductItem(item);
  });
}

function preloadCapsuleSprites() {
  const image = new Image();
  image.decoding = 'async';
  const release = () => {
    image.onload = null;
    image.onerror = null;
    pendingImages.delete(image);
  };
  image.onload = release;
  image.onerror = release;
  pendingImages.set(image, null);
  image.src = capsuleSprite;
}

function buildCapsuleMixer() {
  if (capsuleMixerField.childElementCount) return;
  const fragment = document.createDocumentFragment();

  capsuleColors.forEach((color, index) => {
    const ball = document.createElement('span');
    ball.className = 'capsule-mixer__ball';
    ball.style.setProperty('--ball-index', index);
    ball.style.setProperty('--ball-color', color);
    ball.style.setProperty('--mix-delay', `${-(index * 73)}ms`);
    ball.style.setProperty('--mix-speed', `${520 + (index % 4) * 74}ms`);
    fragment.appendChild(ball);
  });

  capsuleMixerField.appendChild(fragment);
}

function clearCategoryDrawTimers() {
  categoryDrawTimers.forEach(clearLater);
  categoryDrawTimers = [];
}

function clearCategoryDraw({ revealPending = false } = {}) {
  clearCategoryDrawTimers();
  stage.classList.remove('is-mixing', 'is-selecting-capsule', 'is-screen-dropping', 'is-dropping', 'is-open-it');

  if (revealPending && pendingCategoryProduct) {
    const item = pendingCategoryProduct;
    currentProduct = item;
    setTextImmediately(statusCopy, 'OPEN IT · TAP SCREEN TO VIEW');
    stage.classList.add('has-preview', 'is-open-it');
  }

  pendingCategoryProduct = null;
  busy = false;
}

function measureVisibleArtwork(src) {
  if (destroyed) return Promise.reject(new Error('gashapon app destroyed'));
  if (!src) return Promise.resolve({ naturalWidth: 1, naturalHeight: 1, left: 0, top: 0, right: 1, bottom: 1 });
  if (productBoundsCache.has(src)) return productBoundsCache.get(src);

  const measurement = new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      pendingImages.delete(image);
    };
    pendingImages.set(image, reject);
    /* Shopify product images are CDN-hosted. Anonymous CORS keeps the canvas
       readable so transparent padding can be measured instead of shrinking
       the visible artwork on first load. The catch below remains the fallback. */
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      cleanup();
      if (destroyed) {
        reject(new Error('gashapon app destroyed'));
        return;
      }
      const naturalWidth = image.naturalWidth || image.width;
      const naturalHeight = image.naturalHeight || image.height;
      const analysisScale = Math.min(1, 512 / Math.max(naturalWidth, naturalHeight));
      const width = Math.max(1, Math.round(naturalWidth * analysisScale));
      const height = Math.max(1, Math.round(naturalHeight * analysisScale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });

      if (!context) {
        resolve({ naturalWidth, naturalHeight, left: 0, top: 0, right: naturalWidth, bottom: naturalHeight });
        return;
      }

      try {
        context.drawImage(image, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height).data;
        let left = width;
        let top = height;
        let right = -1;
        let bottom = -1;

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            if (pixels[(y * width + x) * 4 + 3] <= 12) continue;
            if (x < left) left = x;
            if (x > right) right = x;
            if (y < top) top = y;
            if (y > bottom) bottom = y;
          }
        }

        if (right < left || bottom < top) {
          resolve({ naturalWidth, naturalHeight, left: 0, top: 0, right: naturalWidth, bottom: naturalHeight });
          return;
        }

        resolve({
          naturalWidth,
          naturalHeight,
          left: left / analysisScale,
          top: top / analysisScale,
          right: (right + 1) / analysisScale,
          bottom: (bottom + 1) / analysisScale
        });
      } catch (error) {
        resolve({ naturalWidth, naturalHeight, left: 0, top: 0, right: naturalWidth, bottom: naturalHeight });
      } finally {
        canvas.width = 1;
        canvas.height = 1;
      }
    };
    image.onerror = error => {
      cleanup();
      reject(error);
    };
    image.src = src;
  });

  const retryableMeasurement = measurement.catch(error => {
    productBoundsCache.delete(src);
    throw error;
  });

  productBoundsCache.set(src, retryableMeasurement);
  return retryableMeasurement;
}

function resetProductArtworkFit() {
  productImages.forEach(image => {
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.left = '0';
    image.style.top = '0';
  });
}

function assignProductImage(image, src, token) {
  image.onerror = () => {
    if (destroyed || token !== productLayoutToken || !src || image.dataset.u20Retry === src) return;
    image.dataset.u20Retry = src;
    later(() => {
      if (!destroyed && token === productLayoutToken) image.src = src;
    }, 180);
  };
  image.onload = () => {
    if (destroyed) return;
    if (image.dataset.u20Retry === src) delete image.dataset.u20Retry;
  };
  image.src = src;
}

function applyVisibleArtworkFit(src, token = productLayoutToken) {
  return measureVisibleArtwork(src).then(bounds => {
    if (destroyed || token !== productLayoutToken || !currentProduct || currentProduct[2] !== src) return false;

    const frameWidth = productVisual.clientWidth;
    const frameHeight = productVisual.clientHeight;
    if (!frameWidth || !frameHeight) return false;

    const visibleWidth = Math.max(1, bounds.right - bounds.left);
    const visibleHeight = Math.max(1, bounds.bottom - bounds.top);
    const visibleAspect = visibleWidth / visibleHeight;
    const isSpineRing = src.includes('product-tokyo-spine-ring');
    const safeWidthRatio = isSpineRing
      ? .54
      : visibleAspect >= 1.65
      ? .64
      : visibleAspect >= 1.12
        ? .72
        : .90;
    const safeWidth = frameWidth * safeWidthRatio;
    const safeHeight = frameHeight * (isSpineRing ? .82 : .94);
    const scale = Math.min(safeHeight / visibleHeight, safeWidth / visibleWidth);
    const artworkCenterX = (bounds.left + bounds.right) / 2;
    const renderedWidth = bounds.naturalWidth * scale;
    const renderedHeight = bounds.naturalHeight * scale;
    const left = frameWidth / 2 - artworkCenterX * scale;
    const top = (frameHeight - visibleHeight * scale) / 2 - bounds.top * scale;

    productImages.forEach(image => {
      image.style.width = `${renderedWidth}px`;
      image.style.height = `${renderedHeight}px`;
      image.style.left = `${left}px`;
      image.style.top = `${top}px`;
    });
    return true;
  }).catch(() => false);
}

function playProductGlitch() {
  clearLater(productGlitchTimer);
  productVisual.classList.remove('glitch-a', 'glitch-b');
  if (!reducedMotion.matches && stage.classList.contains('is-visible')) {
    void productVisual.offsetWidth;
    productVisual.classList.add(Math.random() < 0.5 ? 'glitch-a' : 'glitch-b');
    productGlitchTimer = later(() => {
      productVisual.classList.remove('glitch-a', 'glitch-b');
    }, 820);
  }
}

function scrambleTo(element, finalText, type = 'title') {
  const previous = scrambleJobs.get(element);
  if (previous) window.cancelAnimationFrame(previous);

  const text = String(finalText || '');
  element.dataset.finalText = text;

  if (!text || reducedMotion.matches) {
    element.textContent = text;
    element.classList.remove('is-scrambling');
    return;
  }

  const chars = type === 'price' ? PRICE_CHARS : TITLE_CHARS;
  const duration = type === 'title' ? 900 : type === 'price' ? 720 : 580;
  const start = performance.now();
  element.classList.add('is-scrambling');

  const frame = now => {
    const progress = Math.min(1, (now - start) / duration);
    const reveal = Math.floor(text.length * Math.pow(progress, 1.45));
    let output = '';

    for (let index = 0; index < text.length; index += 1) {
      const character = text[index];
      if (/\s/.test(character) || index < reveal || progress === 1) output += character;
      else output += randomCharacter(chars);
    }

    element.textContent = output;

    if (progress < 1) {
      const next = window.requestAnimationFrame(frame);
      scrambleJobs.set(element, next);
      return;
    }

    element.textContent = text;
    element.classList.remove('is-scrambling');
    scrambleJobs.delete(element);
  };

  const next = window.requestAnimationFrame(frame);
  scrambleJobs.set(element, next);
}

function powerOn() {
  if (powered || starting) return;
  starting = true;
  preloadNextProductImages('all', 2);
  preloadCapsuleSprites();
  buildCapsuleMixer();

  const parts = ['left', 'top', 'screen', 'right-upper', 'right-lower'];
  const shuffled = parts
    .map(name => ({ name, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(item => item.name);

  shuffled.forEach((name, index) => {
    const jitter = Math.round(Math.random() * 34);
    const duration = 740 + Math.round(Math.random() * 230);
    stage.style.setProperty(`--boot-delay-${name}`, `${index * 26 + jitter}ms`);
    stage.style.setProperty(`--boot-duration-${name}`, `${duration}ms`);
  });

  stage.classList.remove('is-off');
  stage.classList.add('is-starting');
  setStatus('SYSTEM BOOTING...');

  later(() => {
    stage.classList.remove('is-starting');
    stage.classList.add('is-on');
    powered = true;
    starting = false;
    setStatus('TURN KNOB TO BROWSE');
  }, 1450);
}

function renderProduct(item) {
  currentProduct = item;
  const layoutToken = ++productLayoutToken;
  const titleLength = item[0].replace(/\s/g, '').length;
  const titleSize = titleLength > 27
    ? 'clamp(5.2px,1.18vw,6.8px)'
    : titleLength > 20
      ? 'clamp(5.8px,1.32vw,7.5px)'
      : titleLength > 14
        ? 'clamp(6.7px,1.55vw,8.5px)'
        : 'clamp(7.8px,1.85vw,10px)';

  productName.style.setProperty('--screen-title-size', titleSize);
  productPrice.style.setProperty('--screen-price-size', 'clamp(8px,2.0vw,11px)');
  resetProductArtworkFit();
  productImages.forEach(image => {
    assignProductImage(image, item[2], layoutToken);
  });
  productVisual.classList.add('is-measuring');
  applyVisibleArtworkFit(item[2], layoutToken).catch(() => false).finally(() => {
    if (layoutToken !== productLayoutToken) return;
    productVisual.classList.remove('is-measuring');
    playProductGlitch();
  });
  scrambleTo(productName, formatProductTitle(item[0]), 'title');
  scrambleTo(productPrice, item[1], 'price');
}

function formatProductTitle(title) {
  const text = String(title || '').trim();
  const words = text.split(/\s+/);
  if (text.length <= 18 || words.length < 2) return text;

  let bestIndex = 1;
  let smallestDifference = Infinity;

  for (let index = 1; index < words.length; index += 1) {
    const firstLine = words.slice(0, index).join(' ');
    const secondLine = words.slice(index).join(' ');
    const difference = Math.abs(firstLine.length - secondLine.length);
    if (difference < smallestDifference) {
      smallestDifference = difference;
      bestIndex = index;
    }
  }

  return `${words.slice(0, bestIndex).join(' ')}\n${words.slice(bestIndex).join(' ')}`;
}

if ('ResizeObserver' in window) {
  productFrameObserver = new ResizeObserver(() => {
    if (destroyed || !currentProduct) return;
    applyVisibleArtworkFit(currentProduct[2]);
  });
  productFrameObserver.observe(productVisual);
} else {
  onWindowResize = () => {
    if (destroyed || !currentProduct) return;
    applyVisibleArtworkFit(currentProduct[2]);
  };
  window.addEventListener('resize', onWindowResize, { passive: true, signal: lifecycleSignal });
}

function toggleCategory() {
  if (!powered || starting || busy) return;
  const previousCategory = category;
  category = category === 'accessories' ? 'toys' : 'accessories';
  preloadNextProductImages(category, 2);
  preloadCapsuleSprites();
  buildCapsuleMixer();
  stage.dataset.category = category;
  stage.classList.add('is-category');
  stage.classList.remove('has-preview', 'has-capsule', 'is-dropping', 'is-open-it');
  currentProduct = null;
  setStatus(category === 'accessories'
    ? 'ACCESSORIES · TURN KNOB TO BROWSE'
    : 'TOYS · TURN KNOB TO BROWSE');
  trackEasterLever(previousCategory, category);
}

function saveReturnState() {
  const state = {
    category,
    currentProduct,
    knobAngle,
    hasCapsule: stage.classList.contains('has-capsule'),
    openIt: stage.classList.contains('is-open-it'),
    capsuleIndex: selectedCapsuleIndex,
    nextCapsuleIndex,
    drawState: Object.fromEntries(Object.entries(drawState).map(([key, value]) => [key, {
      queue: value.queue,
      last: value.last
    }]))
  };
  returnStateMemory = state;
  try {
    window.sessionStorage.setItem(RETURN_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    // The in-memory copy still covers normal back-forward cache restores.
  }
}

function consumeReturnState() {
  let state = returnStateMemory;
  returnStateMemory = null;
  try {
    const storedState = window.sessionStorage.getItem(RETURN_STATE_KEY);
    if (storedState) state = JSON.parse(storedState);
    window.sessionStorage.removeItem(RETURN_STATE_KEY);
  } catch (error) {
    // Storage can be unavailable in private or embedded browsers.
  }
  return state;
}

function setTextImmediately(element, text) {
  const job = scrambleJobs.get(element);
  if (job) window.cancelAnimationFrame(job);
  scrambleJobs.delete(element);
  element.textContent = text;
  element.classList.remove('is-scrambling');
}

function restoreReturnState(state) {
  if (!state || !Array.isArray(state.currentProduct)) return;
  powered = true;
  starting = false;
  busy = false;
  currentProduct = state.currentProduct;
  category = state.category === 'accessories' || state.category === 'toys' ? state.category : null;
  knobAngle = Number.isFinite(state.knobAngle) ? state.knobAngle : 0;
  setOutletCapsuleColor(Number.isInteger(state.capsuleIndex) ? state.capsuleIndex : 1);
  nextCapsuleIndex = Number.isInteger(state.nextCapsuleIndex)
    ? Math.max(0, Math.min(capsuleColors.length - 1, state.nextCapsuleIndex))
    : (selectedCapsuleIndex + 1) % capsuleColors.length;

  stage.className = category
    ? 'machine-stage is-category has-preview'
    : 'machine-stage is-on has-preview';
  if (state.hasCapsule) stage.classList.add('has-capsule');
  if (category) stage.dataset.category = category;
  else stage.removeAttribute('data-category');
  stage.style.setProperty('--knob-angle', `${knobAngle}deg`);

  if (state.drawState) {
    Object.keys(drawState).forEach(key => {
      if (!state.drawState[key]) return;
      drawState[key].queue = Array.isArray(state.drawState[key].queue) ? state.drawState[key].queue : [];
      drawState[key].last = state.drawState[key].last || null;
    });
  }

  /* Returning from a product detail page restores the actual product card,
     not the pre-open capsule message. The selected category, capsule color,
     product and traversal queues are still preserved. */
  renderProduct(state.currentProduct);
  setTextImmediately(productName, formatProductTitle(state.currentProduct[0]));
  setTextImmediately(productPrice, state.currentProduct[1]);
  setTextImmediately(statusCopy, category
    ? 'TAP SCREEN TO VIEW · TURN AGAIN'
    : 'ALL PRODUCTS · TAP SCREEN TO VIEW');
  preloadNextProductImages(category || 'all', 2);
}

function openCurrentProduct() {
  if (!powered || starting || busy || !currentProduct) return;
  const url = currentProduct[3];
  saveReturnState();
  if (window.U20VendingTunnel?.open?.(url)) return;
  window.location.href = url;
}

function turnKnob() {
  if (!powered || starting || busy) return;
  const scope = category || 'all';
  const item = drawNextProduct(scope);
  if (!item) return;

  if (category) {
    startCategoryDraw(item);
    return;
  }

  knobAngle += 230;
  stage.style.setProperty('--knob-angle', `${knobAngle}deg`);
  stage.classList.add('is-turning', 'has-preview');
  stage.classList.remove('has-capsule', 'is-dropping');
  renderProduct(item);
  setStatus('BROWSING...');

  clearLater(knobReleaseTimer);

  knobReleaseTimer = later(() => {
    stage.classList.remove('is-turning');
  }, 420);

  setStatus('ALL PRODUCTS · TAP SCREEN TO VIEW');
}

function startCategoryDraw(item) {
  clearCategoryDraw();
  busy = true;
  pendingCategoryProduct = item;
  currentProduct = null;
  buildCapsuleMixer();

  const mixingDuration = reducedMotion.matches ? 420 : 3000;
  const selectionDuration = 1500;
  const screenDropDuration = reducedMotion.matches ? 170 : 470;
  const outletDuration = reducedMotion.matches ? 300 : 860;
  // Cycle through all twelve supplied colors exactly once before repeating.
  const selectedIndex = nextCapsuleIndex;
  nextCapsuleIndex = (nextCapsuleIndex + 1) % capsuleColors.length;

  capsuleMixerSelected.style.setProperty('--ball-color', capsuleColors[selectedIndex]);
  setOutletCapsuleColor(selectedIndex);
  stage.style.setProperty('--category-mix-duration', `${mixingDuration}ms`);
  stage.style.setProperty('--screen-ball-select-duration', `${selectionDuration}ms`);
  stage.style.setProperty('--screen-ball-drop-duration', `${screenDropDuration}ms`);
  stage.style.setProperty('--outlet-drop-duration', `${outletDuration}ms`);
  stage.classList.remove('has-capsule', 'is-selecting-capsule', 'is-screen-dropping', 'is-dropping', 'is-open-it');
  stage.classList.add('has-preview', 'is-mixing');
  setStatus('CAPSULE MIXING...');

  void stage.offsetWidth;
  knobAngle += reducedMotion.matches ? 360 : 1310;
  stage.style.setProperty('--knob-angle', `${knobAngle}deg`);

  categoryDrawTimers.push(later(() => {
    stage.classList.remove('is-mixing');
    stage.classList.add('is-selecting-capsule');
    setStatus('CAPSULE LOCKED...');
  }, mixingDuration));

  categoryDrawTimers.push(later(() => {
    stage.classList.remove('is-selecting-capsule');
    stage.classList.add('is-screen-dropping');
    setStatus('DISPENSING...');
  }, mixingDuration + selectionDuration));

  categoryDrawTimers.push(later(() => {
    stage.classList.remove('is-dropping');
    void outletCapsule.offsetWidth;
    stage.classList.add('is-dropping');
  }, mixingDuration + selectionDuration + Math.max(0, screenDropDuration - 40)));

  categoryDrawTimers.push(later(() => {
    stage.classList.remove('is-screen-dropping');
    stage.classList.remove('is-dropping');
    stage.classList.add('has-capsule');
    currentProduct = item;
    stage.classList.add('is-open-it');
    pendingCategoryProduct = null;
    busy = false;
    setStatus('OPEN IT · TAP SCREEN TO VIEW');
  }, mixingDuration + selectionDuration + Math.max(0, screenDropDuration - 40) + outletDuration));
}

function beginScreenSwipe(event) {
  if (!powered || starting || category || !event.isPrimary) return;
  const edgeGuard = 28;
  if (event.clientX <= edgeGuard || event.clientX >= window.innerWidth - edgeGuard) return;

  screenSwipe = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    startedAt: performance.now(),
    movedHorizontally: false
  };
  try {
    domeHotspot.setPointerCapture(event.pointerId);
  } catch (error) {
    // Pointer capture is only a reliability enhancement; the gesture still works without it.
  }
}

function trackScreenSwipe(event) {
  if (!screenSwipe || screenSwipe.pointerId !== event.pointerId) return;
  const deltaX = event.clientX - screenSwipe.startX;
  const deltaY = event.clientY - screenSwipe.startY;
  if (Math.abs(deltaX) >= 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
    screenSwipe.movedHorizontally = true;
  }
}

function finishScreenSwipe(event) {
  if (!screenSwipe || screenSwipe.pointerId !== event.pointerId) return;
  const gesture = screenSwipe;
  screenSwipe = null;

  const deltaX = event.clientX - gesture.startX;
  const deltaY = event.clientY - gesture.startY;
  const elapsed = performance.now() - gesture.startedAt;
  const isHorizontalSwipe = Math.abs(deltaX) >= 22
    && Math.abs(deltaX) > Math.abs(deltaY) * 1.15
    && elapsed <= 1200;

  if (gesture.movedHorizontally) lastScreenSwipeAt = performance.now();
  if (!isHorizontalSwipe || category) return;
  if (event.cancelable) event.preventDefault();
  lastScreenSwipeAt = performance.now();
  turnKnob();
}

function cancelScreenSwipe(event) {
  if (!screenSwipe || screenSwipe.pointerId !== event.pointerId) return;
  screenSwipe = null;
}

const onDomeClick = event => {
  if (performance.now() - lastScreenSwipeAt < 650) {
    event.preventDefault();
    return;
  }
  openCurrentProduct();
};

powerHotspot.addEventListener('click', powerOn, { signal: lifecycleSignal });
topLogoHotspot.addEventListener('click', playEasterEgg, { signal: lifecycleSignal });
leverHotspot.addEventListener('click', toggleCategory, { signal: lifecycleSignal });
knobHotspot.addEventListener('click', turnKnob, { signal: lifecycleSignal });
domeHotspot.addEventListener('pointerdown', beginScreenSwipe, { signal: lifecycleSignal });
domeHotspot.addEventListener('pointermove', trackScreenSwipe, { signal: lifecycleSignal });
domeHotspot.addEventListener('pointerup', finishScreenSwipe, { signal: lifecycleSignal });
domeHotspot.addEventListener('pointercancel', cancelScreenSwipe, { signal: lifecycleSignal });
domeHotspot.addEventListener('click', onDomeClick, { signal: lifecycleSignal });
outletHotspot.addEventListener('click', openCurrentProduct, { signal: lifecycleSignal });

scrambleTo(statusCopy, statusCopy.textContent, 'status');

const onVisibilityChange = () => {
  if (destroyed) return;
  if (document.hidden) {
    cancelEasterEgg();
    stage.classList.remove('is-turning', 'is-dropping', 'is-visible');
    clearCategoryDraw({ revealPending: true });
    return;
  }

  const bounds = stage.getBoundingClientRect();
  const visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
  stage.classList.toggle('is-visible', visible);
};

const onPageHide = () => {
  clearLater(productGlitchTimer);
  clearLater(knobReleaseTimer);
  clearCategoryDrawTimers();
  cancelEasterEgg();
  [statusCopy, productName, productPrice].forEach(element => {
    const job = scrambleJobs.get(element);
    if (job) window.cancelAnimationFrame(job);
    scrambleJobs.delete(element);
  });
  stageObserver.disconnect();
};

const onPageShow = (event) => {
  if (destroyed) return;
  restoreReturnState(consumeReturnState());
  stageObserver.observe(stage);
  const bounds = stage.getBoundingClientRect();
  stage.classList.toggle('is-visible', bounds.bottom > 0 && bounds.top < window.innerHeight && !document.hidden);
  if (event.persisted) {
    window.requestAnimationFrame(() => {
      window.U20MobileScrollRecovery?.recover?.('capsule-product-return', {forceThemeUnlock: true});
    });
  }
};

document.addEventListener('visibilitychange', onVisibilityChange, { signal: lifecycleSignal });
window.addEventListener('pagehide', onPageHide, { signal: lifecycleSignal });
window.addEventListener('pageshow', onPageShow, { signal: lifecycleSignal });

function destroy() {
  if (destroyed) return;
  destroyed = true;
  lifecycleController.abort();
  stageObserver.disconnect();
  productFrameObserver?.disconnect();
  activeTimers.forEach(timer => window.clearTimeout(timer));
  activeTimers.clear();
  clearEasterTimers();
  categoryDrawTimers = [];
  screenSwipe = null;
  productLayoutToken += 1;
  [statusCopy, productName, productPrice].forEach(element => {
    const job = scrambleJobs.get(element);
    if (job) window.cancelAnimationFrame(job);
    scrambleJobs.delete(element);
    element.classList.remove('is-scrambling');
  });
  pendingImages.forEach((reject, image) => {
    image.onload = null;
    image.onerror = null;
    image.removeAttribute('src');
    if (typeof reject === 'function') reject(new Error('gashapon app destroyed'));
  });
  pendingImages.clear();
  productImages.forEach(image => {
    image.onload = null;
    image.onerror = null;
  });
  productBoundsCache.clear();
  preloadedProductUrls.clear();
  stage.classList.remove('is-visible', 'is-turning', 'is-dropping', 'is-starting');
}

return { destroy };
};
